import {
    DiscountPreviewStyled,
    ItemStyled,
    NameStyled,
    PriceStyled,
    RemoveButtonStyled,
} from './DiscountPreview.style';
import { useCurrentCart } from 'connectors/cart/Cart';
import { useApplyLoyaltyPointsToCart } from 'hooks/cart/useApplyLoyaltyPointsToCart';
import { useRemovePromoCodeFromCart } from 'hooks/cart/useRemovePromoCodeFromCart';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { RemoveIcon } from 'public/svg/IconsSvg';
import { memo, useCallback } from 'react';

export const DiscountPreview = memo(() => {
    const t = useTypedTranslationFunction();
    const domainConfig = useDomainConfig();
    const { formatPriceNumber, formatPriceObject } = useFormatPrice();
    const { cart, promoCodes } = useCurrentCart();
    const [removePromoCode] = useRemovePromoCodeFromCart();
    const [applyLoyaltyPointsToCart] = useApplyLoyaltyPointsToCart();

    const onRemovePromoCodeHandler = useCallback(
        (promoCode: string) => () => {
            removePromoCode([promoCode], {
                success: t('Promo code was removed from the order.'),
                error: t('There was an error while removing the promo code from the order.'),
            });
        },
        [removePromoCode, t],
    );

    const onRemoveLoyaltyPointsHandler = useCallback(() => {
        applyLoyaltyPointsToCart(0, {
            success: t('Loyalty points was removed from the order.'),
            error: t('There was an error while removing the loyalty points from the order.'),
        });
    }, [applyLoyaltyPointsToCart, t]);

    if (
        cart === null ||
        (promoCodes.length === 0 && (cart.loyaltyClubDiscount === null || cart.loyaltyClubDiscount === 0))
    ) {
        return null;
    }

    return (
        <DiscountPreviewStyled>
            {promoCodes.map(({ code, discount, isGiftVoucher, freeTransportFrom }) => (
                <ItemStyled key={code}>
                    <NameStyled>
                        {isGiftVoucher ? t('Gift voucher') : t('Discount code')} {code}
                    </NameStyled>
                    {freeTransportFrom !== null && getSellingPriceByDomain(discount, domainConfig) !== 0 && (
                        <PriceStyled>
                            {' '}
                            &minus;{formatPriceObject(discount, { explicitZero: true })} +{' '}
                            {t('Free transport above {{ price }}', { price: formatPriceNumber(freeTransportFrom) })}
                        </PriceStyled>
                    )}
                    {freeTransportFrom !== null && getSellingPriceByDomain(discount, domainConfig) === 0 && (
                        <PriceStyled>
                            {t('Free transport above {{ price }}', { price: formatPriceNumber(freeTransportFrom) })}
                        </PriceStyled>
                    )}
                    {freeTransportFrom === null && (
                        <PriceStyled> &minus;{formatPriceObject(discount, { explicitZero: true })}</PriceStyled>
                    )}
                    <RemoveButtonStyled onClick={onRemovePromoCodeHandler(code)}>
                        <RemoveIcon />
                    </RemoveButtonStyled>
                </ItemStyled>
            ))}

            {cart.loyaltyClubDiscount !== null && cart.loyaltyClubDiscount !== 0 && (
                <ItemStyled>
                    <NameStyled>{t('Your applied points')}</NameStyled>
                    <PriceStyled> &minus; {formatPriceNumber(cart.loyaltyClubDiscount)}</PriceStyled>
                    <RemoveButtonStyled onClick={onRemoveLoyaltyPointsHandler}>
                        <RemoveIcon />
                    </RemoveButtonStyled>
                </ItemStyled>
            )}
        </DiscountPreviewStyled>
    );
});

DiscountPreview.displayName = 'DiscountPreview';
